import React, { useEffect, useState } from "react";
import {
  Box,
  VStack,
  Heading,
  Text,
  Flex,
  useColorModeValue,
  Spinner,
  Center,
  Button,
  Icon,
  SimpleGrid,
  Select,
  HStack,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import useAxios from "axios-hooks";
import { useSetRecoilState } from "recoil";
import { TbCreditCardPay } from "react-icons/tb";
import {
  FaCalendarAlt,
  FaMoneyBillWave,
  FaUsers,
  FaChartLine,
  FaRocket,
} from "react-icons/fa";

import { toastState } from "atoms/toastState";
import logger from "infra/logger";
import { getPreviousMonthNameAndYear } from "utils/utils";
import PayrunBox from "../components/PayrunBox";
import PayrunPayoutsTable from "../components/PayrunPayoutsTable";
import PayrunExportButton from "../components/PayrunExportButton";

// Motion components
const MotionBox = motion(Box);

// Glassmorphism Card Component
const GlassMorphismCard = ({ children, ...props }) => {
  const cardBg = useColorModeValue(
    "rgba(255, 255, 255, 0.8)",
    "rgba(26, 32, 44, 0.8)",
  );
  const borderColor = useColorModeValue(
    "rgba(255, 255, 255, 0.2)",
    "rgba(255, 255, 255, 0.1)",
  );

  return (
    <MotionBox
      bg={cardBg}
      backdropFilter="blur(20px)"
      borderRadius="16px"
      border="1px solid"
      borderColor={borderColor}
      boxShadow="0 8px 32px 0 rgba(31, 38, 135, 0.37)"
      p={8}
      w="100%"
      transition={{ duration: 0.3 }}
      whileHover={{ boxShadow: "0 12px 40px 0 rgba(31, 38, 135, 0.45)" }}
      {...props}
    >
      {children}
    </MotionBox>
  );
};

const PayoutRun = () => {
  const [payoutsData, setPayoutsData] = useState({ payrun: [], payouts: [] });
  const [monthYear, setMonthYear] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const setToast = useSetRecoilState(toastState);

  const textColor = useColorModeValue("gray.700", "white");
  const subTextColor = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  // Generate year options (current year and previous years)
  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    // Include current year and previous 5 years
    for (let i = 0; i <= 5; i++) {
      years.push(currentYear - i);
    }
    return years;
  };

  // Get available months based on selected year
  const getAvailableMonths = () => {
    if (!selectedYear) return monthOptions;

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1; // getMonth() returns 0-11

    // If selected year is current year, only show previous months
    if (parseInt(selectedYear) === currentYear) {
      return monthOptions.filter((month) => month.value < currentMonth);
    }

    // If selected year is in the past, show all months
    if (parseInt(selectedYear) < currentYear) {
      return monthOptions;
    }

    // This shouldn't happen as we don't allow future years, but return empty as fallback
    return [];
  };

  // Check if payout generation is allowed for selected month/year
  const isPayoutGenerationAllowed = () => {
    if (!monthYear?.monthNumber || !monthYear?.year) return false;

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    const selectedDate = new Date(monthYear.year, monthYear.monthNumber - 1);
    const currentDate = new Date(currentYear, currentMonth - 1);

    // Only allow if selected date is in the past
    return selectedDate < currentDate;
  };

  // Month options
  const monthOptions = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  const [{ loading: getPayrunLoading }, getPayrun, cancelRequest] = useAxios(
    {
      method: "GET",
      url: `api/payrun?month=${monthYear?.monthNumber}&year=${monthYear?.year}`,
    },
    { manual: true },
  );

  const [{ loading: generatePayrunLoading }, generatePayrun, cancelReq] =
    useAxios(
      {
        method: "POST",
        url: `api/payrun/create?month=${monthYear?.monthNumber}&year=${monthYear?.year}`,
      },
      { manual: true },
    );

  const fetchPayrun = async () => {
    try {
      const response = await getPayrun();
      logger.log("Payrun response data:", response?.data?.data);
      const payoutData = response?.data?.data;
      setPayoutsData({
        payrun: payoutData?.payrunData || [],
        payouts: payoutData?.payoutsList || [],
      });
    } catch (error) {
      logger.error("Error fetching payrun:", error);
      if (error?.code !== "ERR_CANCELED") {
        setToast({
          isOpen: true,
          title: "Error Occurred",
          status: "error",
          description:
            error?.response?.data?.error || "Failed to fetch payrun data",
        });
      }
    }
  };

  const generatePayRun = async () => {
    // Check if payout generation is allowed
    if (!isPayoutGenerationAllowed()) {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().toLocaleString("en-US", {
        month: "long",
      });

      let errorMessage =
        "Payout generation is only allowed for previous months.";

      if (monthYear?.year === currentYear) {
        errorMessage = `For ${currentYear}, payout generation is only available for months before ${currentMonth}.`;
      } else if (monthYear?.year > currentYear) {
        errorMessage = "Payout generation is not allowed for future years.";
      }

      setToast({
        isOpen: true,
        title: "Invalid Selection",
        status: "error",
        description: errorMessage,
      });
      return;
    }

    try {
      await generatePayrun();
      setToast({
        isOpen: true,
        title: "Success",
        status: "success",
        description: "Payrun generated successfully",
      });
      fetchPayrun();
    } catch (error) {
      logger.error("Error generating payrun:", error);
      if (error?.code !== "ERR_CANCELED") {
        setToast({
          isOpen: true,
          title: "Error Occurred",
          status: "error",
          description:
            error?.response?.data?.error || "Failed to generate payrun",
        });
      }
    }
  };

  const getLastMonthYear = () => {
    const prevMonthData = getPreviousMonthNameAndYear();
    const monthInfo = {
      month: prevMonthData?.month,
      year: prevMonthData?.year,
      monthNumber: prevMonthData?.monthNumber,
    };
    setMonthYear(monthInfo);
    setSelectedYear(prevMonthData?.year);
    setSelectedMonth(prevMonthData?.monthNumber);
  };

  const handleMonthChange = (monthNumber) => {
    setSelectedMonth(monthNumber);
    updateMonthYear(monthNumber, selectedYear);
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    // Clear month selection if current month is not available for selected year
    const availableMonths = getAvailableMonthsForYear(year);
    if (
      selectedMonth &&
      !availableMonths.some((m) => m.value === parseInt(selectedMonth))
    ) {
      setSelectedMonth(null);
      setMonthYear(null);
    } else {
      updateMonthYear(selectedMonth, year);
    }
  };

  // Get available months for a specific year
  const getAvailableMonthsForYear = (year) => {
    if (!year) return monthOptions;

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    if (parseInt(year) === currentYear) {
      return monthOptions.filter((month) => month.value < currentMonth);
    }

    if (parseInt(year) < currentYear) {
      return monthOptions;
    }

    return [];
  };

  const updateMonthYear = (monthNumber, year) => {
    if (monthNumber && year) {
      const monthName = monthOptions.find(
        (m) => m.value === parseInt(monthNumber),
      )?.label;
      setMonthYear({
        month: monthName,
        year: parseInt(year),
        monthNumber: parseInt(monthNumber),
      });
    }
  };

  useEffect(() => {
    getLastMonthYear();
  }, []);

  useEffect(() => {
    if (monthYear) {
      fetchPayrun();
      return () => {
        cancelRequest();
        cancelReq();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthYear]);
  if (getPayrunLoading && !payoutsData.payrun.length) {
    return (
      <Box
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Center>
          <VStack spacing={4}>
            <Spinner size="xl" color="purple.500" thickness="4px" />
            <Text color="gray.600" fontSize="lg">
              Loading payout run data...
            </Text>
          </VStack>
        </Center>
      </Box>
    );
  }
  return (
    <Box minH="100vh" w="100%" py={8} px={6}>
      <MotionBox
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        w="100%"
        maxW="none"
      >
        <VStack spacing={8} align="stretch">
          {/* Header */}
          <Flex justify="space-between" align="center" mb={2}>
            <Box>
              <Heading size="lg" color={textColor} fontWeight="bold">
                Payrun
              </Heading>
              <Text color={subTextColor} fontSize="lg">
                Generate and manage monthly payrun
              </Text>
            </Box>
            <VStack align="end" spacing={2}>
              <HStack spacing={3}>
                <Select
                  value={selectedMonth || ""}
                  onChange={(e) => handleMonthChange(e.target.value)}
                  maxW="150px"
                  bg="white"
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="xl"
                  size="sm"
                >
                  <option value="">Select Month</option>
                  {getAvailableMonths().map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </Select>
                <Select
                  value={selectedYear || ""}
                  onChange={(e) => handleYearChange(e.target.value)}
                  maxW="120px"
                  bg="white"
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="xl"
                  size="sm"
                >
                  <option value="">Select Year</option>
                  {generateYearOptions().map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </Select>
                {payoutsData?.payrun?.length > 0 && (
                  <PayrunExportButton
                    payrunId={
                      payoutsData?.payrun[0]?.id || payoutsData?.payrun[0]?.Id
                    }
                    month={monthYear?.month}
                    year={monthYear?.year}
                  />
                )}
              </HStack>
              <HStack spacing={2}>
                <Icon as={FaCalendarAlt} color={subTextColor} />
                <Text fontSize="lg" fontWeight="semibold" color={textColor}>
                  {monthYear?.month} {monthYear?.year}
                </Text>
              </HStack>
            </VStack>
          </Flex>

          {payoutsData?.payrun?.length > 0 ? (
            <>
              {/* Payrun Status Card */}
              <PayrunBox
                payrun={payoutsData?.payrun[0]}
                fetchPayrun={fetchPayrun}
              />

              {/* Payrun Payouts Table */}
              <PayrunPayoutsTable
                payouts={payoutsData?.payouts || []}
                payrunId={
                  payoutsData?.payrun[0]?.id || payoutsData?.payrun[0]?.Id
                }
                month={payoutsData?.payrun[0]?.month}
                year={payoutsData?.payrun[0]?.year}
                totalGrossAmount={payoutsData?.payrun[0]?.total_gross_amount}
                totalNetAmount={payoutsData?.payrun[0]?.total_net_amount}
                payoutCount={payoutsData?.payrun[0]?.payout_count}
              />
            </>
          ) : (
            /* Empty State - Generate Payrun */
            <MotionBox
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <GlassMorphismCard>
                <VStack spacing={8} align="center" textAlign="center">
                  {/* Icon and Title */}
                  <VStack spacing={4}>
                    <Box
                      w="80px"
                      h="80px"
                      borderRadius="full"
                      bg="linear-gradient(135deg, #f57d36 0%, #ff8c42 100%)"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      color="white"
                      boxShadow="xl"
                    >
                      <Icon as={FaRocket} boxSize={10} />
                    </Box>
                    <VStack spacing={2}>
                      <Heading size="lg" color={textColor}>
                        Generate Payouts
                      </Heading>
                      <Text
                        fontSize="xl"
                        color={textColor}
                        fontWeight="semibold"
                      >
                        {monthYear?.month} {monthYear?.year}
                      </Text>
                      <Text color={subTextColor} maxW="md">
                        {isPayoutGenerationAllowed()
                          ? "No payouts exists for this month. Click the button below to generate payouts for all eligible users."
                          : `Payout generation is only allowed for previous months. ${
                              monthYear?.year === new Date().getFullYear()
                                ? `For ${monthYear.year}, only months before ${new Date().toLocaleString("en-US", { month: "long" })} are available.`
                                : "Please select a month from the past."
                            }`}
                      </Text>
                    </VStack>
                  </VStack>

                  {/* Action Button */}
                  <Button
                    size="lg"
                    colorScheme={
                      isPayoutGenerationAllowed() ? "orange" : "gray"
                    }
                    leftIcon={<Icon as={TbCreditCardPay} boxSize={6} />}
                    onClick={generatePayRun}
                    isLoading={generatePayrunLoading}
                    isDisabled={!isPayoutGenerationAllowed()}
                    loadingText="Generating..."
                    px={8}
                    py={6}
                    fontSize="lg"
                    fontWeight="semibold"
                    borderRadius="xl"
                    bg={
                      isPayoutGenerationAllowed()
                        ? "linear-gradient(135deg, #f57d36 0%, #ff8c42 100%)"
                        : "gray.300"
                    }
                    color="white"
                    border="none"
                    _hover={
                      isPayoutGenerationAllowed()
                        ? {
                            transform: "translateY(-2px)",
                            shadow: "xl",
                            bg: "linear-gradient(135deg, #e56d26 0%, #ef7c32 100%)",
                          }
                        : {}
                    }
                    _disabled={{
                      bg: "gray.300",
                      cursor: "not-allowed",
                      transform: "none",
                    }}
                    _active={{
                      transform: "translateY(0px)",
                    }}
                    transition="all 0.2s"
                  >
                    Generate Payouts
                  </Button>

                  {/* Info Cards */}
                  <SimpleGrid
                    columns={{ base: 1, md: 3 }}
                    spacing={4}
                    w="100%"
                    maxW="2xl"
                  >
                    <VStack spacing={2} p={4} bg="blue.50" borderRadius="xl">
                      <Icon as={FaUsers} color="blue.500" boxSize={6} />
                      <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        color="blue.700"
                      >
                        All Eligible Users
                      </Text>
                      <Text fontSize="xs" color="blue.600" textAlign="center">
                        Includes property owners, CPOs, and BCOs
                      </Text>
                    </VStack>
                    <VStack spacing={2} p={4} bg="green.50" borderRadius="xl">
                      <Icon
                        as={FaMoneyBillWave}
                        color="green.500"
                        boxSize={6}
                      />
                      <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        color="green.700"
                      >
                        Automatic Calculation
                      </Text>
                      <Text fontSize="xs" color="green.600" textAlign="center">
                        Based on previous month's transactions
                      </Text>
                    </VStack>
                    <VStack spacing={2} p={4} bg="purple.50" borderRadius="xl">
                      <Icon as={FaChartLine} color="purple.500" boxSize={6} />
                      <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        color="purple.700"
                      >
                        Station-wise Breakdown
                      </Text>
                      <Text fontSize="xs" color="purple.600" textAlign="center">
                        Detailed breakdown for each charging station
                      </Text>
                    </VStack>
                  </SimpleGrid>
                </VStack>
              </GlassMorphismCard>
            </MotionBox>
          )}
        </VStack>
      </MotionBox>
    </Box>
  );
};

export default PayoutRun;
